import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, MenuItem, IconButton, Alert, Tabs, Tab, Checkbox, FormControlLabel } from '@mui/material';
import axios from 'axios';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import EmailIcon from '@mui/icons-material/Email';
import { Snackbar } from '@mui/material';

// 🛠️ NEW HELPER: Currency Adornment for the 'R' symbol
const CurrencyAdornment = () => (
    <Typography variant="body1" sx={{ mr: 1, color: 'text.secondary' }}>R</Typography>
);

const ImmoveableAssetsTab = ({ matter, estateId, assetType }) => {
  const [asset_description, setAssetDescription] = useState("");
  const [occupied, setOccupied] = useState("");
  const [monthlyRentalAmount, setMonthlyRentalAmount] = useState("");
  const [section80ApprovalDate, setSection80ApprovalDate] = useState("");
  const [auctioneer, setAuctioneer] = useState("");
  const [auctionDate, setAuctionDate] = useState("");
  const [purchaser, setPurchaser] = useState("");
  const [purchased_date, setPurchasedDate] = useState("");
  const [sold_by, setSoldBy] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [attorney, setAttorney] = useState("");
  const [address, setAddress] = useState("");
  const [phoneNo, setPhoneNo] = useState("");
  const [emailAddress2, setEmailAddress2] = useState("");
  const [emailAddress3, setEmailAddress3] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [transactionInstructionDate, setTransactionInstructionDate] = useState("");
  const [transferDate, setTransferDate] = useState("");
  const [costsReceivedDate, setCostsReceivedDate] = useState("");
  const [section89Costs, setSection89Costs] = useState("");
  const [provisionalDividendPaid, setProvisionalDividendPaid] = useState("");
  const [finalPaymentDate, setFinalPaymentDate] = useState("");
  const [bondBankName, setBondBankName] = useState("");
  const [bondAccountNo, setBondAccountNo] = useState("");
  const [bondNo, setBondNo] = useState("");
  const [bondAddress, setBondAddress] = useState("");
  const [bondAdditionalAddress, setBondAdditionalAddress] = useState("");
  const [bondFaxNumber, setBondFaxNumber] = useState("");
  const [bondPhoneNo, setBondPhoneNo] = useState("");
  const [bondEmailAddress, setBondEmailAddress] = useState("");
  const [bondClaimProved, setBondClaimProved] = useState("");
  const [bondClaimAmount, setBondClaimAmount] = useState("");
  const [bondReference, setBondReference] = useState("");
  const [comments, setComments] = useState("");
  const [claimApproved, setClaimApproved] = useState(false);
  const [claimRejected, setClaimRejected] = useState(false);
  const [contactPerson, setContactPerson] = useState('');
  const [contactPersonEmail, setContactPersonEmail] = useState('');
  const [contactPerson2, setContactPerson2] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');


  const [user, setUser] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [selectedTransactionIndex, setSelectedTransactionIndex] = useState(null);
  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [estate, setEstate] = useState(null);
  const [assetCategory, setAssetCategory] = useState('');
  const [payments, setPayments] = useState('');

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const encumberedTabLabels = ["Encumbered 1", "Encumbered 2", "Encumbered 3", "Encumbered 4"];
  const freeresidueTabLabels = ["Free Residue 1", "Free Residue 2", "Free Residue 3", "Free Residue 4"];
  const [selectedTab, setSelectedTab] = useState(0);

  const legalMatterId = matter?.id || "";
  const estateIdFromMatter = matter?.EstateID || estateId || "";

  const currentTabLabel =
  assetType === "encumbered"
    ? encumberedTabLabels[selectedTab] || ""
    : freeresidueTabLabels[selectedTab] || "";

  const normalizeStatus = (label) => label?.toLowerCase().replace(/\s+/g, '-');

  // Updated statusMap
  const statusMap = {
    encumbered: encumberedTabLabels.map(normalizeStatus),
    freeresidue: freeresidueTabLabels.map(normalizeStatus),
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const fetchUser = async () => {
    try {
      const res = await axios.get("https://ipms.insollex.com/api/user", { withCredentials: true });
      if (typeof res.data !== "object" || res.data === null) {
        setUser(null);
        return;
      }
      if (!res.data.role) {
        res.data.role = "guest";
      }
      setUser(res.data);
    } catch {
      setUser(null);
    }
  };

// Fetch assets function
const fetchAssets = async () => {
  try {
    if (!assetType) {
      setTableData([]);
      setSnackbarMessage('Please select an asset type.');
      setSnackbarSeverity('warning');
      setOpenSnackbar(true);
      return;
    }

    // Safely normalize assetType
    const key = assetType ? assetType.replace(/\s/g, '').toLowerCase() : null;
    const tabLength = key && statusMap[key] ? statusMap[key].length : 0;

    if (!key || typeof selectedTab !== 'number' || selectedTab < 0 || selectedTab >= tabLength) {
      console.warn('Invalid tab selected:', selectedTab);
      setTableData([]);
      setSnackbarMessage('Invalid tab selected.');
      setSnackbarSeverity('warning');
      setOpenSnackbar(true);
      return;
    }

    const statusParam = statusMap[key][selectedTab];
    if (!statusParam) {
      console.warn('No status defined for the selected tab:', selectedTab);
      setTableData([]);
      setSnackbarMessage('No status defined for the selected tab.');
      setSnackbarSeverity('warning');
      setOpenSnackbar(true);
      return;
    }

    console.log('Fetching assets with:', { legalMatterId, estateIdFromMatter, statusParam });

    let data = [];

    if (legalMatterId) {
      const endpoint = 'https://ipms.insollex.com/api/immoveables';
      const response = await axios.get(endpoint, { params: { legalMatterId, status: statusParam } });
      data = Array.isArray(response.data) ? response.data : [];
      console.log('Received data:', data);
    } else if (estateIdFromMatter && estateIdFromMatter !== '0' && key === 'encumbered') {
      // const oldEndpoint = `https://ipms.insollex.com/api/old-database/properties/${estateIdFromMatter}`;
      // const response = await axios.get(oldEndpoint);
      // data = Array.isArray(response.data) ? response.data : [];
      // console.log('Received data from old database:', data);
    } else {
      setTableData([]);
      setSnackbarMessage('Please provide either a valid Legal Matter ID or Estate ID.');
      setSnackbarSeverity('warning');
      setOpenSnackbar(true);
      return;
    }

    if (data.length > 0) {
      setTableData(data);

      // Auto-fill form fields with the first record
      const selected = data[0];

      setAttorney(selected.attorney || '');
      setAssetCategory(selected.assetCategory || '');
      setPurchaser(selected.purchaser || '');
      setPurchasedDate(selected.purchased_date || '');
      setSoldBy(selected.sold_by || '');
      setAssetDescription(selected.asset_description || '');
      setOccupied(selected.occupied || '');
      setMonthlyRentalAmount(selected.monthly_rental_amount || '');
      setSection80ApprovalDate(selected.section_80_approval_date || '');
      setAuctioneer(selected.auctioneer || '');
      setAuctionDate(selected.auction_date || '');
      setPurchasePrice(selected.purchase_price || '');
      setEmailAddress(selected.email_address || '');
      setAddress(selected.address || '');
      setPhoneNo(selected.phone_no || '');
      setEmailAddress2(selected.email_address2 || '');
      setEmailAddress3(selected.email_address3 || '');
      setMobileNumber(selected.mobile_number || '');
      setTransactionInstructionDate(selected.transaction_instruction_date || '');
      setTransferDate(selected.transfer_date || '');
      setCostsReceivedDate(selected.costs_received_date || '');
      setSection89Costs(selected.section_89_costs || '');
      setProvisionalDividendPaid(selected.provisional_dividend_paid || '');
      setFinalPaymentDate(selected.final_payment_date || '');
      setBondBankName(selected.bondBankName || '');
      setBondAccountNo(selected.bondAccountNo || '');
      setBondNo(selected.bondNo || '');
      setBondAddress(selected.bondAddress || '');
      setBondAdditionalAddress(selected.bondAdditionalAddress || '');
      setBondFaxNumber(selected.bondFaxNumber || '');
      setBondPhoneNo(selected.bondPhoneNo || '');
      setBondEmailAddress(selected.bondEmailAddress || '');
      setBondClaimProved(selected.bondClaimProved || '');
      setBondClaimAmount(selected.bondClaimAmount || '');
      setBondReference(selected.bondReference || '');
      setComments(selected.comments || '');
      setContactPerson(selected.contactPerson || '');
      setContactPerson2(selected.contactPerson2 || '');
      setContactPersonEmail(selected.contactPersonEmail || '');
      setRegistrationNumber(selected.registrationNumber || '');

      setSelectedTransactionIndex(0);
    } else {
      setTableData([]);
      resetForm();
      setSnackbarMessage('No assets found for the selected tab.');
      setSnackbarSeverity('warning');
      setOpenSnackbar(true);
    }
  } catch (error) {
    console.error('Error fetching assets:', error);
    setSnackbarMessage('Error fetching assets.');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  }
};

  
  

  const sendAssetEmail = async () => {
    if (!emailAddress) return;
    try {
      await axios.post("https://ipms.insollex.com/api/immoveable-assets/email", {
        to: emailAddress,
        asset_description,
      });
      alert("Email sent!");
    } catch (err) {
      console.error("Error sending email:", err);
      alert("Failed to send email.");
    }
  };

  const resetForm = () => {
    setAssetDescription("");
    setOccupied("");
    setMonthlyRentalAmount("");
    setSection80ApprovalDate("");
    setAuctioneer("");
    setAuctionDate("");
    setPurchaser("");
    setPurchasedDate("");
    setSoldBy("");
    setPurchasePrice("");
    setEmailAddress("");
    setAttorney("");
    setAddress("");
    setPhoneNo("");
    setEmailAddress2("");
    setEmailAddress3("");
    setMobileNumber("");
    setTransactionInstructionDate("");
    setTransferDate("");
    setCostsReceivedDate("");
    setSection89Costs("");
    setProvisionalDividendPaid("");
    setFinalPaymentDate("");
    setBondBankName("");
    setBondAccountNo("");
    setBondNo("");
    setBondAddress("");
    setBondAdditionalAddress("");
    setBondFaxNumber("");
    setBondPhoneNo("");
    setBondEmailAddress("");
    setBondClaimProved("");
    setBondClaimAmount("");
    setBondReference("");
    setComments("");
    setAssetCategory("");     // ✅ reset category
    setPayments("");          // ✅ reset payments
    setContactPerson("");
    setContactPerson2("");     // ✅ reset contact person
    setContactPersonEmail("");
    setRegistrationNumber(""); 
    setSelectedTransactionIndex(null);
  };

  const sanitizeAsset = (asset) => ({
    ...asset,
    bondClaimAmount: asset.bondClaimAmount ?? "",
  });

  const handleAddOrSaveTransaction = async () => {
    try {
      const normalizedStatus = normalizeStatus(currentTabLabel);
  
      if (!normalizedStatus) {
        setSnackbarMessage("Invalid status - cannot save.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }
  
      if (!legalMatterId) {
        setSnackbarMessage("Legal Matter ID is missing.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
        return;
      }
  
      const newTransaction = {
        attorney,
        assetCategory,
        payments,
        contactPerson,
        contactPerson2,
        contactPersonEmail,
        purchaser,
        purchased_date,
        sold_by,
        asset_description,
        occupied,
        monthly_rental_amount: monthlyRentalAmount,
        section_80_approval_date: section80ApprovalDate,
        auctioneer,
        auction_date: auctionDate,
        purchase_price: purchasePrice,
        email_address: emailAddress,
        address,
        phone_no: phoneNo,
        email_address2: emailAddress2,
        email_address3: emailAddress3,
        mobile_number: mobileNumber,
        transaction_instruction_date: transactionInstructionDate,
        transfer_date: transferDate,
        costs_received_date: costsReceivedDate,
        section_89_costs: section89Costs,
        provisional_dividend_paid: provisionalDividendPaid,
        final_payment_date: finalPaymentDate,
        comments,
        registrationNumber,
        bondBankName,
        bondAccountNo,
        bondNo,
        bondAddress,
        bondAdditionalAddress,
        bondFaxNumber,
        bondPhoneNo,
        bondEmailAddress,
        bondClaimProved,
        bondClaimAmount,
        bondReference,
        legalMatterId,
        status: normalizedStatus,
      };
  
      let response;
      if (selectedTransactionIndex !== null) {
        // Update
        response = await axios.put(
          `https://ipms.insollex.com/api/immoveables/${tableData[selectedTransactionIndex].id}`,
          newTransaction
        );
  
        const updated = sanitizeAsset(response.data);
        const updatedTableData = [...tableData];
        updatedTableData[selectedTransactionIndex] = updated;
        setTableData(updatedTableData);
      } else {
        // Add
        response = await axios.post(
          "https://ipms.insollex.com/api/immoveables",
          newTransaction
        );
  
        const added = sanitizeAsset(response.data);
        setTableData([...tableData, added]);
        setSelectedTransactionIndex(tableData.length);
      }
  
      // ✅ Refresh filtered list properly (no duplicates)
      const normalizedTab = normalizeStatus(currentTabLabel);
      setFilteredAssets(
        [...tableData].filter((asset) => asset.status?.toLowerCase() === normalizedTab)
      );
  
      setSnackbarMessage("Transaction saved successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Error saving transaction:", error);
      setSnackbarMessage("Error saving transaction.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };
  
  
  const handleEditTransaction = (index) => {
    const selected = tableData[index];
    if (!selected) return;
  
    setAttorney(selected.attorney || "");
    setPurchaser(selected.purchaser || "");
    setPurchasedDate(selected.purchased_date || "");
    setSoldBy(selected.sold_by || "");
    setAssetDescription(selected.asset_description || "");
    setOccupied(selected.occupied || "");
    setMonthlyRentalAmount(selected.monthly_rental_amount || "");
    setSection80ApprovalDate(selected.section_80_approval_date || "");
    setAuctioneer(selected.auctioneer || "");
    setAuctionDate(selected.auction_date || "");
    setPurchasePrice(selected.purchase_price || "");
    setEmailAddress(selected.email_address || "");
    setAddress(selected.address || "");
    setPhoneNo(selected.phone_no || "");
    setEmailAddress2(selected.email_address2 || "");
    setEmailAddress3(selected.email_address3 || "");
    setMobileNumber(selected.mobile_number || "");
    setTransactionInstructionDate(selected.transaction_instruction_date || "");
    setTransferDate(selected.transfer_date || "");
    setCostsReceivedDate(selected.costs_received_date || "");
    setSection89Costs(selected.section_89_costs || "");
    setProvisionalDividendPaid(selected.provisional_dividend_paid || "");
    setFinalPaymentDate(selected.final_payment_date || "");
    setBondBankName(selected.bondBankName || "");
    setBondAccountNo(selected.bondAccountNo || "");
    setBondNo(selected.bondNo || "");
    setBondAddress(selected.bondAddress || "");
    setBondAdditionalAddress(selected.bondAdditionalAddress || "");
    setBondFaxNumber(selected.bondFaxNumber || "");
    setBondPhoneNo(selected.bondPhoneNo || "");
    setBondEmailAddress(selected.bondEmailAddress || "");
    setBondClaimProved(selected.bondClaimProved || "");
    setBondClaimAmount(selected.bondClaimAmount || "");
    setBondReference(selected.bondReference || "");
    setComments(selected.comments || "");
    setAssetCategory(selected.assetCategory || "");   // ✅ restore category
    setPayments(selected.payments || "");             // ✅ restore payments
    setContactPerson(selected.contactPerson || "");   // ✅ restore contact person
    setContactPersonEmail(selected.contactPersonEmail || "");
    setRegistrationNumber(selected.registrationNumber || "");
  
    setSelectedTransactionIndex(index);
  };
  

  const handleDeleteTransaction = async (index) => {
    const transactionId = tableData[index].id;
    try {
      await axios.delete(`https://ipms.insollex.com/api/immoveables/${transactionId}`);
      setTableData(tableData.filter((_, idx) => idx !== index));
      setSnackbarMessage("Transaction deleted successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Error deleting transaction:", error);
      setSnackbarMessage("Error deleting transaction.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  // Fetch user on mount
  useEffect(() => {
    fetchUser();
  }, []);

  // Fetch assets when assetType, selectedTab, or IDs change
  useEffect(() => {
    if (assetType && (legalMatterId || estateIdFromMatter)) {
      fetchAssets();
    }
  }, [assetType, selectedTab, legalMatterId, estateIdFromMatter]);
  

  useEffect(() => {
    if (Array.isArray(tableData)) {
      const normalizedTab = normalizeStatus(currentTabLabel);
  
      const filteredAssets = tableData.filter(asset =>
        asset.status?.toLowerCase() === normalizedTab
      );
  
      setFilteredAssets(filteredAssets);
    } else {
      setFilteredAssets([]);
    }
  }, [tableData, currentTabLabel]);
  

  
  return (
    <Box p={1}>
      {assetType && ['encumbered', 'free residue'].includes(assetType) && (
        <>
          {/* Tabs */}
          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={selectedTab} onChange={handleTabChange} centered>
                  {(statusMap[assetType?.replace(/\s/g, '').toLowerCase()] || []).map(
                    (label, index) => <Tab key={index} label={label} value={index} />
                  )}
                </Tabs>
              </Box>
            </Grid>
          </Grid>
  
          <Box p={3}>
            {/* Asset Description */}
            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1">Asset Description</Typography>
              <Grid container spacing={2}>
                {/* Asset Category */}
                <Grid item xs={3}>
                  <TextField
                    select
                    fullWidth
                    label="Asset Category"
                    value={assetCategory}
                    onChange={(e) => setAssetCategory(e.target.value)}
                    disabled={user?.role === 'guest' || user?.isInvited}
                  >
                    <MenuItem value="Immovable Property">Immovable Property</MenuItem>
                    <MenuItem value="Vehicles">Vehicles</MenuItem>
                    <MenuItem value="Others">Others</MenuItem>
                  </TextField>
                </Grid>
  
                {/* Occupied & Monthly Rental (not Vehicles) */}
                {assetCategory !== 'Vehicles' && (
                  <>
                    <Grid item xs={3}>
                      <TextField
                        select
                        fullWidth
                        label="Occupied"
                        value={occupied}
                        onChange={(e) => setOccupied(e.target.value)}
                        disabled={user?.role === 'guest' || user?.isInvited}
                      >
                        <MenuItem value="Tenant">Tenant</MenuItem>
                        <MenuItem value="Insolvent">Insolvent</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                      </TextField>
                    </Grid>
  
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        label="Monthly Rental Amount"
                        type="number" // Set type to number
                        value={monthlyRentalAmount}
                        onChange={(e) => setMonthlyRentalAmount(e.target.value)}
                        disabled={user?.role === 'guest' || user?.isInvited}
                        // 🛠️ ADDED: Currency Adornment
                        InputProps={{ startAdornment: <CurrencyAdornment /> }}
                      />
                    </Grid>
                  </>
                )}
  
                {/* 🚗 ADDED: Registration Number (Vehicles only) */}
                {assetCategory === 'Vehicles' && (
                  <Grid item xs={3}>
                    <TextField
                      fullWidth
                      label="Registration Number"
                      value={registrationNumber}
                      onChange={(e) => setRegistrationNumber(e.target.value)}
                      disabled={user?.role === 'guest' || user?.isInvited}
                    />
                  </Grid>
                )}
  
                {/* Asset Description Field */}
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Asset Description"
                    value={asset_description}
                    onChange={(e) => setAssetDescription(e.target.value)}
                    disabled={user?.role === 'guest' || user?.isInvited}
                  />
                </Grid>
              </Grid>
            </Paper>
  
            {/* ❌ REMOVED FOR 'free residue': Bond Holder / Creditors Information */}
            {(assetType !== 'free residue' && (assetCategory === 'Immovable Property' || assetCategory === 'Others' || assetCategory === 'Vehicles')) && (
              <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {assetCategory === 'Vehicles' ? 'Creditors Information' : 'Bond Holder Information'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Name"
                      value={bondBankName}
                      onChange={(e) => setBondBankName(e.target.value)}
                      disabled={user?.role === 'guest' || user?.isInvited}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label={assetCategory === 'Vehicles' ? "Account No / Creditor Ref" : "Bond Account No"}
                      value={bondAccountNo}
                      onChange={(e) => setBondAccountNo(e.target.value)}
                      disabled={user?.role === 'guest' || user?.isInvited}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      value={emailAddress3}
                      onChange={(e) => setEmailAddress3(e.target.value)}
                      disabled={user?.role === 'guest' || user?.isInvited}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Phone No"
                      value={bondPhoneNo}
                      onChange={(e) => setBondPhoneNo(e.target.value)}
                      disabled={user?.role === 'guest' || user?.isInvited}
                    />
                  </Grid>
                  {assetCategory !== 'Vehicles' && (
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Claim Recived"
                        value={bondClaimProved}
                        onChange={(e) => setBondClaimProved(e.target.value)}
                        disabled={user?.role === 'guest' || user?.isInvited}
                      />
                    </Grid>
                  )}
                  {assetCategory !== 'Vehicles' && (
                    <Grid item xs={4}>
                      {/* NOTE: You have two text fields with label "Claim Proved" using the same state 'bondClaimProved'. 
                             One should probably be "Claim Received" if that's what "Claim Recived" was intended for. 
                             I'll leave it as you had it, but this is a potential bug.
                      */}
                      <TextField
                        fullWidth
                        label="Claim Proved"
                        value={bondClaimProved}
                        onChange={(e) => setBondClaimProved(e.target.value)}
                        disabled={user?.role === 'guest' || user?.isInvited}
                      />
                    </Grid>
                  )}
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Claim Amount"
                      type="number" // Set type to number
                      value={bondClaimAmount}
                      onChange={(e) => setBondClaimAmount(e.target.value)}
                      disabled={user?.role === 'guest' || user?.isInvited}
                      // 🛠️ ADDED: Currency Adornment
                      InputProps={{ startAdornment: <CurrencyAdornment /> }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={bondAddress}
                      onChange={(e) => setBondAddress(e.target.value)}
                      disabled={user?.role === 'guest' || user?.isInvited}
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}
  
            {/* Sale Of Asset */}
            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1">Sale Of Asset</Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    select
                    fullWidth
                    label="Method of Sale"
                    value={auctioneer}
                    onChange={(e) => setAuctioneer(e.target.value)}
                    disabled={user?.role === 'guest' || user?.isInvited}
                  >
                    <MenuItem value="Auction">Auction</MenuItem>
                    <MenuItem value="Private Treaty">Private Treaty</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Auction Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={auctionDate}
                    onChange={(e) => setAuctionDate(e.target.value)}
                    disabled={user?.role === 'guest' || user?.isInvited}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    label="Contact Person"
                    value={contactPerson}
                    onChange={(e) => setContactPerson(e.target.value)}
                    disabled={user?.role === 'guest' || user?.isInvited}
                  />
                </Grid>
                <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    color="primary"
                    onClick={() => {
                      if (contactPersonEmail) {
                        window.location.href = `mailto:${contactPersonEmail}`;
                      }
                    }}
                    disabled={!contactPersonEmail}
                  >
                    {/* Assuming EmailIcon is imported */}
                    <EmailIcon />
                  </IconButton>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Purchased Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={purchased_date}
                    onChange={(e) => setPurchasedDate(e.target.value)}
                    disabled={user?.role === 'guest' || user?.isInvited}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Purchase Price"
                    type="number" // Set type to number
                    value={purchasePrice}
                    onChange={(e) => setPurchasePrice(e.target.value)}
                    disabled={user?.role === 'guest' || user?.isInvited}
                    // 🛠️ ADDED: Currency Adornment
                    InputProps={{ startAdornment: <CurrencyAdornment /> }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    disabled={user?.role === 'guest' || user?.isInvited}
                  />
                </Grid>
              </Grid>
            </Paper>
  
            {/* Transfer Information (only for Immovable Assets) */}
            {assetCategory === 'Immovable Property' && (
              <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Transfer Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField fullWidth label="Attorney" value={attorney} onChange={(e) => setAttorney(e.target.value)} />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField fullWidth label="Contact Person" value={contactPerson2} onChange={(e) => setContactPerson2(e.target.value)} />
                  </Grid>
                  <Grid item xs={1} display="flex" alignItems="center">
                    <IconButton
                      color="primary"
                      onClick={() => {
                        if (emailAddress2) {
                          window.location.href = `mailto:${emailAddress2}`;
                        }
                      }}
                      disabled={!emailAddress2}
                    >
                      {/* Assuming EmailIcon is imported */}
                      <EmailIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth label="Phone No" value={phoneNo} onChange={(e) => setPhoneNo(e.target.value)} />
                  </Grid>
                  {/* Additional transfer fields */}
                  <Grid item xs={4}>
                    <TextField fullWidth label="Section 80 Approval Date" type="date" InputLabelProps={{ shrink: true }} value={section80ApprovalDate} onChange={(e) => setSection80ApprovalDate(e.target.value)} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth label="Transaction Instruction Date" type="date" InputLabelProps={{ shrink: true }} value={transactionInstructionDate} onChange={(e) => setTransactionInstructionDate(e.target.value)} />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField fullWidth label="Transfer Date" type="date" InputLabelProps={{ shrink: true }} value={transferDate} onChange={(e) => setTransferDate(e.target.value)} />
                  </Grid>
                  {/* 🛠️ ADDED: Section 89 Costs field which was missing from JSX */}
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Section 89 Costs"
                      type="number"
                      value={section89Costs}
                      onChange={(e) => setSection89Costs(e.target.value)}
                      InputProps={{ startAdornment: <CurrencyAdornment /> }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            )}
  
            {/* Comments */}
            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1">Comments</Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                disabled={user?.role === 'guest' || user?.isInvited}
              />
            </Paper>
  
            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-start">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleAddOrSaveTransaction}
                  disabled={user?.role === 'guest' || user?.isInvited}
                >
                  {selectedTransactionIndex === null ? 'Save' : 'Update'}
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleCancelEdit}
                  disabled={user?.role === 'guest' || user?.isInvited}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Box>
        </>
      )}
  
      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
  

export default ImmoveableAssetsTab;